import { HttpMethod, IHttpClient } from '@dirootie-http/http-types';
import { useContainer } from './use-container.hook';
import { Params } from 'react-router-dom';
import {
  ApiServiceGeneric,
  IApiServiceGeneric,
} from '@dirootie-http/api-types';
import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { useEffect } from 'react';
import { useNotifications } from './use-notifications.hook';
import {
  createdErrorNotification,
  saveErrorNotification,
  saveSuccessNotification,
} from '@dirootie-web/web-components-types';
import { useNavigation } from './use-navigation.hook';
import { buildPathFromObject } from '@dirootie-core/api-types';
import { createdSuccessNotification } from '@dirootie-web/components-types';
import { DataEndpoint } from '@components-common/routes';

// Define a hook to inject dependencies from the container
export const useHttpClient = () => {
  const container = useContainer();
  return container.resolve<IHttpClient>('http-client');
};

export const useApiGenericClient = () => {
  const container = useContainer();
  return container.resolve<ApiServiceGeneric>('api-service');
};

export const getById = <T>(
  apiClient: ApiServiceGeneric,
  template: string,
  id: string,
  params?: any
) => {
  return apiClient.get<T>(id, {
    params,
    template,
    method: HttpMethod.GET,
  } as any);
};

export const useGetElementById = <T>(
  dataSource: DataEndpoint,
  id: string,
  params?: Params
) => {
  const apiClient = useApiGenericClient();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(
    [dataSource.id, id],
    () => getById<T>(apiClient, dataSource.template, id, dataSource.params),
    {
      keepPreviousData: true,
      staleTime: 10000,
    }
  );

  useEffect(() => {
    if (data) {
      queryClient.fetchQuery([dataSource.id, id], () =>
        getById(apiClient, dataSource.template, id, dataSource.params)
      );
    }
  }, [
    queryClient,
    data,
    id,
    apiClient,
    params,
    dataSource.template,
    dataSource.id,
    dataSource.params,
  ]);
  return { data, isLoading };
};

export const createElement = <T>(
  apiClient: ApiServiceGeneric,
  template: string,
  entity: T,
  params?: any
) => {
  return apiClient.create<T>(entity, {
    params,
    template,
    method: HttpMethod.POST,
  } as any);
};

export const useCreateElement = <T>(
  dataSource: DataEndpoint,
  routeToAfterCreation: string,
  params?: Params
): UseMutationResult => {
  const apiClient = useApiGenericClient();
  const notificationService = useNotifications();
  const navigator = useNavigation();
  const { isLoading, isError, error, mutate } = useMutation({
    mutationKey: dataSource.id,
    mutationFn: (entity: any) => {
      return createElement<T>(
        apiClient,
        dataSource.template,
        entity,
        dataSource.params
      )
        .then((x: any) => {
          notificationService.success(createdSuccessNotification);
          navigator.navigateToPath(
            buildPathFromObject(x, routeToAfterCreation),
            { relative: true }
          );
          return Promise.resolve(x);
        })
        .catch((x: any) =>
          notificationService.error(createdErrorNotification(''))
        );
    },
  });
  return { isLoading, isError, error, mutate } as any;
};

export const updateElement = <T>(
  apiClient: ApiServiceGeneric,
  template: string,
  entity: T,
  params?: any
) => {
  return apiClient.update<T>(entity, {
    params,
    template,
    method: HttpMethod.PUT,
  } as any);
};

export const useUpdateElement = <T>(
  dataSource: DataEndpoint,
  params?: Params
): UseMutationResult => {
  const apiClient = useApiGenericClient();
  const notificationService = useNotifications();
  const { isLoading, isError, error, mutate } = useMutation({
    mutationKey: params!.id,
    mutationFn: (entity: any) => {
      return updateElement<T>(
        apiClient,
        dataSource.template,
        entity,
        dataSource.params
      )
        .then((x) => {
          notificationService.success(saveSuccessNotification);
          return Promise.resolve(x);
        })
        .catch((x) => notificationService.error(saveErrorNotification));
    },
  });
  return { isLoading, isError, error, mutate } as any;
};
